/*
 * LogisticsWalletPage — wallet dashboard with admin allocation, team visibility,
 * and rich transaction records showing who spent, what, and payment details.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Snowflake, Coins, ArrowUp, ArrowDown, ShieldCheck, UsersThree, User as UserIcon,
  PlusCircle, CreditCard, Receipt, CaretDown, CaretUp, Eye, Clock, Wallet,
  ArrowCounterClockwise, Warning, CheckCircle, Spinner, Crown, XCircle,
} from '@phosphor-icons/react';
import LogisticsLayout, { formatMinor } from './LogisticsLayout';
import { useLogisticsExercise } from '../../context/LogisticsExerciseContext';
import logisticsApi from '../../lib/logisticsApi';
import SimCard from '../../components/logistics/SimCard';
import { useAuth } from '../../context/AuthContext';

const SYM = 'Ÿ';

const ADMIN_ROLES = ['super_admin', 'hub_admin'];

const TYPE_LABELS = {
  allocate: 'Allocation',
  hold: 'Hold',
  capture: 'Capture',
  release: 'Release',
  refund: 'Refund',
  topup: 'Top-up',
  fine: 'Fine / Deduction',
  adjust: 'Adjustment',
};

const TYPE_ICONS = {
  allocate: PlusCircle,
  hold: Clock,
  capture: CreditCard,
  release: ArrowCounterClockwise,
  refund: ArrowCounterClockwise,
  topup: PlusCircle,
  fine: Warning,
  adjust: ArrowDown,
};

/* ======== Wallet Card ======== */
const WalletCard = ({ w, onSelect, selected }) => {
  const pct = w.allocated > 0 ? Math.round(((w.spent + w.held) / w.allocated) * 100) : 0;

  return (
    <motion.button
      onClick={() => onSelect(w)}
      layout
      className={`ls-surface ls-noise text-left p-5 w-full transition-all ${selected ? 'ring-2 ring-[var(--ls-primary)]' : ''}`}
      style={selected ? { borderColor: 'var(--ls-primary)' } : {}}
      data-testid={`ls-wallet-card-${w.wallet_id}`}
    >
      <div className="flex items-center gap-2 mb-3">
        {w.owner_type === 'team' ? (
          <UsersThree size={18} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
        ) : (
          <Coins size={18} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
        )}
        <span className="ls-overline">
          {w.owner_type === 'user' ? 'Personal' : (w.team_name || 'Team')} Wallet
        </span>
        {w.frozen && <Snowflake size={14} weight="fill" style={{ color: 'var(--ls-danger)' }} />}
      </div>
      {w.exercise_name && (
        <p className="text-[11px] font-medium mb-1" style={{ color: 'var(--ls-primary)' }}>
          {w.exercise_name}
        </p>
      )}
      <p className="ls-mono text-3xl font-bold" style={{ color: 'var(--ls-secondary)' }}>
        {SYM}{formatMinor(w.available)}
      </p>
      <p className="text-xs mt-1" style={{ color: 'var(--ls-muted)' }}>
        Allocated {SYM}{formatMinor(w.allocated)} · Held {SYM}{formatMinor(w.held)} · Spent {SYM}{formatMinor(w.spent)}
      </p>
      {/* Spend bar */}
      <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--ls-elevated)' }}>
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${Math.min(pct, 100)}%`,
            background: pct > 85 ? 'var(--ls-danger)' : 'var(--ls-primary)',
          }}
        />
      </div>
      <p className="text-[10px] mt-1" style={{ color: 'var(--ls-muted)' }}>{pct}% utilised</p>
    </motion.button>
  );
};

/* ======== Team Members Panel ======== */
const TeamMembersPanel = ({ walletId }) => {
  const [members, setMembers] = useState([]);
  const [teamName, setTeamName] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!walletId) return;
    let c = false;
    (async () => {
      try {
        const res = await logisticsApi.walletMembers(walletId);
        if (!c) {
          setMembers(res.members || []);
          setTeamName(res.team_name);
        }
      } catch { if (!c) setMembers([]); }
    })();
    return () => { c = true; };
  }, [walletId]);

  if (members.length === 0) return null;

  return (
    <div className="ls-surface p-4 mb-6">
      <button
        className="flex items-center gap-2 w-full text-left"
        onClick={() => setOpen(v => !v)}
      >
        <UsersThree size={16} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
        <span className="ls-overline flex-1">{teamName || 'Team'} · {members.length} members</span>
        {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 space-y-2">
              {members.map(m => (
                <div key={m.user_id} className="flex items-center gap-2 text-sm py-1">
                  <UserIcon size={14} weight="fill" style={{ color: 'var(--ls-muted)' }} />
                  <span style={{ color: 'var(--ls-secondary)' }}>{m.name}</span>
                  <span className="text-xs" style={{ color: 'var(--ls-muted)' }}>{m.email}</span>
                  {m.is_lead && (
                    <Crown size={12} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] mt-3" style={{ color: 'var(--ls-muted)' }}>
              All members share this wallet. Any member's spend deducts from the shared balance.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ======== Admin Allocation Panel ======== */
const AllocationPanel = ({ exerciseId, onAllocated }) => {
  const [ownerType, setOwnerType] = useState('team');
  const [ownerId, setOwnerId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (!exerciseId) return;
    (async () => {
      try {
        const res = await logisticsApi.walletsForExercise(exerciseId);
        const teamWallets = (res || []).filter(w => w.owner_type === 'team');
        setTeams(teamWallets);
      } catch { /* ignore */ }
    })();
  }, [exerciseId]);

  const allocate = async () => {
    if (!ownerId.trim() || !amount || Number(amount) <= 0) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const w = await logisticsApi.allocateWallet(exerciseId, {
        owner_type: ownerType,
        owner_id: ownerId.trim(),
        amount: Number(amount),
        reason: reason.trim() || undefined,
      });
      setResult(w);
      setAmount('');
      setReason('');
      onAllocated?.();
    } catch (e) {
      setError(e.message || 'Allocation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ls-elevated p-5 mb-8">
      <div className="flex items-center gap-2 mb-4">
        <PlusCircle size={18} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
        <h3 className="ls-heading text-lg font-bold">Allocate Budget</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <label className="block">
          <span className="ls-overline mb-1 block">Recipient type</span>
          <select value={ownerType} onChange={e => setOwnerType(e.target.value)} className="ls-input w-full">
            <option value="team">Team</option>
            <option value="user">Individual</option>
          </select>
        </label>
        <label className="block">
          <span className="ls-overline mb-1 block">{ownerType === 'team' ? 'Team ID' : 'User ID'}</span>
          <input
            type="text"
            value={ownerId}
            onChange={e => setOwnerId(e.target.value)}
            placeholder={ownerType === 'team' ? 'ls_team_...' : 'user_id'}
            className="ls-input w-full ls-mono text-sm"
          />
        </label>
        <label className="block">
          <span className="ls-overline mb-1 block">Amount ({SYM})</span>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="10000.00"
            className="ls-input w-full ls-mono"
          />
        </label>
        <label className="block">
          <span className="ls-overline mb-1 block">Reason (optional)</span>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Initial exercise budget"
            className="ls-input w-full text-sm"
          />
        </label>
      </div>

      {teams.length > 0 && ownerType === 'team' && (
        <div className="mb-3">
          <span className="ls-overline text-[10px] block mb-1">Quick pick existing team:</span>
          <div className="flex flex-wrap gap-1.5">
            {teams.map(t => (
              <button
                key={t.owner_id}
                className={`text-xs px-2.5 py-1 rounded-sm transition-colors ${
                  ownerId === t.owner_id
                    ? 'bg-[var(--ls-primary)] text-white'
                    : 'bg-[var(--ls-elevated)] text-[var(--ls-muted)] hover:text-[var(--ls-secondary)]'
                }`}
                onClick={() => setOwnerId(t.owner_id)}
              >
                {t.owner_id.slice(-8)}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        className="ls-btn-primary"
        onClick={allocate}
        disabled={submitting || !ownerId.trim() || !amount || Number(amount) <= 0}
      >
        {submitting ? <Spinner size={14} className="animate-spin" /> : <PlusCircle size={14} weight="bold" />}
        {submitting ? 'Allocating…' : `Allocate ${SYM}${amount || '0.00'}`}
      </button>

      {error && (
        <p className="text-sm mt-3 flex items-center gap-1.5" style={{ color: 'var(--ls-danger)' }}>
          <XCircle size={14} weight="fill" /> {error}
        </p>
      )}
      {result && (
        <p className="text-sm mt-3 flex items-center gap-1.5" style={{ color: 'var(--ls-success)' }}>
          <CheckCircle size={14} weight="fill" />
          Allocated {SYM}{formatMinor(result.allocated)} → available {SYM}{formatMinor(result.available)}
        </p>
      )}
    </div>
  );
};

/* ======== Rich Transaction Row ======== */
const TxnRow = ({ t }) => {
  const [expanded, setExpanded] = useState(false);
  const isNeg = t.amount < 0;
  const Icon = TYPE_ICONS[t.type] || ArrowDown;
  const label = TYPE_LABELS[t.type] || t.type;
  const actor = t.actor;
  const booking = t.booking_detail;

  return (
    <>
      <tr
        className="border-t cursor-pointer hover:bg-[var(--ls-accent)] transition-colors"
        style={{ borderColor: 'var(--ls-border)' }}
        onClick={() => setExpanded(v => !v)}
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <Icon size={14} weight="duotone" style={{ color: isNeg ? 'var(--ls-danger)' : 'var(--ls-success)' }} />
            <span className="ls-overline text-xs">{label}</span>
          </div>
        </td>
        <td className="py-3 px-4 text-xs" style={{ color: 'var(--ls-muted)' }}>
          {actor?.name || actor?.email || '—'}
        </td>
        <td className="py-3 px-4 text-xs" style={{ color: 'var(--ls-muted)' }}>
          {t.service || '—'}
        </td>
        <td className="py-3 px-4 text-xs" style={{ color: 'var(--ls-muted)' }}>
          {new Date(t.created_at).toLocaleString()}
        </td>
        <td className="py-3 px-4 text-right ls-mono font-medium" style={{ color: isNeg ? 'var(--ls-danger)' : 'var(--ls-success)' }}>
          {isNeg ? <ArrowDown size={11} weight="bold" className="inline mr-0.5" /> : <ArrowUp size={11} weight="bold" className="inline mr-0.5" />}
          {SYM}{formatMinor(Math.abs(t.amount))}
        </td>
        <td className="py-3 px-4 text-right ls-mono text-xs" style={{ color: 'var(--ls-secondary)' }}>
          {SYM}{formatMinor(t.balance_after)}
        </td>
        <td className="py-3 px-4 text-center">
          {(booking || t.card_id || t.reference_id) ? (
            expanded ? <CaretUp size={12} /> : <CaretDown size={12} />
          ) : null}
        </td>
      </tr>
      {expanded && (booking || t.card_id || t.reference_id) && (
        <tr>
          <td colSpan="7" className="px-4 pb-4" style={{ background: 'var(--ls-elevated)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 py-3">
              {/* Actor details */}
              {actor && (
                <div>
                  <p className="ls-overline text-[10px] mb-0.5">Transacted by</p>
                  <p className="text-sm font-medium" style={{ color: 'var(--ls-secondary)' }}>{actor.name}</p>
                  <p className="text-xs" style={{ color: 'var(--ls-muted)' }}>{actor.email}</p>
                </div>
              )}
              {/* Payment card */}
              {(t.card_id || booking?.card_last4) && (
                <div>
                  <p className="ls-overline text-[10px] mb-0.5">Payment card</p>
                  <div className="flex items-center gap-1.5">
                    <CreditCard size={14} style={{ color: 'var(--ls-primary)' }} />
                    <span className="ls-mono text-sm">
                      {booking?.card_last4 ? `•••• ${booking.card_last4}` : (t.card_id || '—')}
                    </span>
                  </div>
                </div>
              )}
              {/* Reference */}
              {t.reference_id && (
                <div>
                  <p className="ls-overline text-[10px] mb-0.5">Reference</p>
                  <p className="ls-mono text-xs" style={{ color: 'var(--ls-muted)' }}>
                    {t.reference_type}: {t.reference_id}
                  </p>
                </div>
              )}
              {/* Booking details */}
              {booking && (
                <>
                  <div>
                    <p className="ls-overline text-[10px] mb-0.5">Venue</p>
                    <p className="text-sm font-medium" style={{ color: 'var(--ls-secondary)' }}>{booking.venue_name || '—'}</p>
                  </div>
                  <div>
                    <p className="ls-overline text-[10px] mb-0.5">Stay</p>
                    <p className="text-xs" style={{ color: 'var(--ls-muted)' }}>
                      {booking.check_in} → {booking.check_out} · {booking.rooms} room{booking.rooms > 1 ? 's' : ''} · {booking.guests} guest{booking.guests > 1 ? 's' : ''}
                    </p>
                  </div>
                  <div>
                    <p className="ls-overline text-[10px] mb-0.5">Booking total</p>
                    <p className="ls-mono text-sm font-semibold" style={{ color: 'var(--ls-primary)' }}>
                      {SYM}{formatMinor(booking.total_minor)}
                    </p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${
                      booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                </>
              )}
              {/* Fingerprint reason */}
              {t.fingerprint?.reason && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="ls-overline text-[10px] mb-0.5">Reason</p>
                  <p className="text-xs italic" style={{ color: 'var(--ls-muted)' }}>{t.fingerprint.reason}</p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/* ======== Multi-Card Display ======== */
const CardsSection = ({ walletId, selectedCardId, onSelectCard }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!walletId) return;
    let c = false;
    setLoading(true);
    (async () => {
      try {
        const res = await logisticsApi.walletCards(walletId);
        if (!c) setCards(res || []);
      } catch { if (!c) setCards([]); }
      if (!c) setLoading(false);
    })();
    return () => { c = true; };
  }, [walletId]);

  if (loading) return null;
  if (cards.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CreditCard size={16} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
          <span className="ls-overline">Payment Cards · {cards.length}</span>
        </div>
        {selectedCardId && (
          <button
            className="text-xs px-3 py-1 rounded-sm transition-colors"
            style={{ background: 'var(--ls-elevated)', color: 'var(--ls-primary)' }}
            onClick={() => onSelectCard(null)}
          >
            Show all transactions
          </button>
        )}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3" style={{ scrollSnapType: 'x mandatory' }}>
        {cards.map((c) => (
          <div
            key={c.card_id}
            className="flex-shrink-0"
            style={{ width: 280, scrollSnapAlign: 'start' }}
          >
            <SimCard
              card={c}
              holder={c.holder}
              label={`Card ${c.card_index + 1}`}
              selected={selectedCardId === c.card_id}
              onClick={() => onSelectCard(selectedCardId === c.card_id ? null : c.card_id)}
            />
          </div>
        ))}
      </div>
      <p className="text-[11px] mt-1 max-w-md" style={{ color: 'var(--ls-muted)' }}>
        Tap a card to flip it and see CVV. Click to filter transactions by that card.
      </p>
    </div>
  );
};

/* ======== Main Page ======== */
const WalletContent = () => {
  const { user } = useAuth();
  const { selectedId: ctxExerciseId } = useLogisticsExercise();
  const [wallets, setWallets] = useState({ personal: [], team: [] });
  const [selected, setSelected] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);

  const isAdmin = ADMIN_ROLES.some(r => (user?.roles || []).includes(r));

  const loadWallets = useCallback(async () => {
    setLoading(true);
    try {
      const w = await logisticsApi.myWallets();
      const filterByEx = (list) =>
        ctxExerciseId ? (list || []).filter(x => x.logistics_exercise_id === ctxExerciseId) : (list || []);
      const filtered = {
        personal: filterByEx(w.personal),
        team: filterByEx(w.team),
      };
      setWallets(filtered);
      const first = filtered.personal?.[0] || filtered.team?.[0];
      if (first && !selected) setSelected(first);
      else if (!first) setSelected(null);
    } catch (e) {
      setErr(e.message || 'Failed to load wallets');
    } finally {
      setLoading(false);
    }
  }, [ctxExerciseId]);

  useEffect(() => { setSelected(null); setSelectedCardId(null); loadWallets(); }, [ctxExerciseId]);

  useEffect(() => {
    if (!selected) { setTxns([]); return; }
    let c = false;
    (async () => {
      try {
        const t = selectedCardId
          ? await logisticsApi.cardTransactions(selected.wallet_id, selectedCardId)
          : await logisticsApi.walletTransactions(selected.wallet_id);
        if (!c) setTxns(t);
      } catch { if (!c) setTxns([]); }
    })();
    return () => { c = true; };
  }, [selected?.wallet_id, selectedCardId]);

  const allWallets = [...(wallets.personal || []), ...(wallets.team || [])];
  const totalAvailable = allWallets.reduce((s, w) => s + (w.available || 0), 0);

  const refreshAfterAllocation = () => {
    setLoading(true);
    setSelected(null);
    loadWallets();
  };

  return (
    <>
      <section className="mb-8">
        <span className="ls-overline flex items-center gap-2 mb-3" style={{ color: 'var(--ls-primary)' }}>
          <ShieldCheck size={12} weight="fill" /> WALLET · KOMOMO DOLLARS (SC)
        </span>
        <h1 className="ls-heading text-3xl sm:text-4xl font-bold tracking-tight">Finance command centre.</h1>
        <p className="text-sm mt-2 max-w-xl" style={{ color: 'var(--ls-muted)' }}>
          Track allocations, team spending, and every transaction in Komomo Dollars ({SYM}).
          Personal and team pools shown separately — every credit that moves is written into an immutable trail.
        </p>
      </section>

      {/* Admin allocation panel */}
      {isAdmin && ctxExerciseId && (
        <AllocationPanel exerciseId={ctxExerciseId} onAllocated={refreshAfterAllocation} />
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-8">
          <Spinner size={18} className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
          <p className="text-sm" style={{ color: 'var(--ls-muted)' }}>Loading wallets…</p>
        </div>
      ) : err ? (
        <div className="ls-surface p-6" style={{ borderColor: 'var(--ls-danger)' }}>
          <p className="text-sm" style={{ color: 'var(--ls-danger)' }} data-testid="ls-wallet-error">{err}</p>
        </div>
      ) : allWallets.length === 0 ? (
        <div className="ls-elevated p-10 text-center" data-testid="ls-wallet-empty">
          <Wallet size={48} weight="duotone" style={{ color: 'var(--ls-primary)' }} className="mx-auto mb-3" />
          <p className="ls-heading text-xl font-semibold mb-1">No wallets provisioned.</p>
          <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--ls-muted)' }}>
            {isAdmin
              ? 'Use the allocation panel above to create a wallet and assign a starting budget.'
              : 'Ask a SimCell operator to allocate a starting budget. Your personal and team pools will appear here the moment credits are issued.'}
          </p>
        </div>
      ) : (
        <>
          {/* Wallet grid */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {allWallets.map((w) => (
              <WalletCard key={w.wallet_id} w={w} onSelect={(wal) => { setSelectedCardId(null); setSelected(wal); }} selected={selected?.wallet_id === w.wallet_id} />
            ))}
          </section>

          {selected && (
            <>
              {/* Team members (for team wallets) */}
              {selected.owner_type === 'team' && (
                <TeamMembersPanel walletId={selected.wallet_id} />
              )}

              {/* Payment cards */}
              <CardsSection
                walletId={selected.wallet_id}
                selectedCardId={selectedCardId}
                onSelectCard={(cardId) => setSelectedCardId(cardId)}
              />

              {/* Ledger */}
              <section>
                <div className="flex items-baseline justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Receipt size={18} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
                    <h2 className="ls-heading text-xl font-semibold">Transaction Ledger</h2>
                    {selectedCardId && (
                      <span className="text-xs px-2 py-0.5 rounded-sm" style={{ background: 'var(--ls-primary)', color: '#fff' }}>
                        Card ····{selectedCardId.slice(-4)}
                      </span>
                    )}
                  </div>
                  <span className="ls-overline">
                    {selected ? `Wallet · ${selected.wallet_id.slice(-6)}` : 'Select a wallet'}
                  </span>
                </div>
                <div className="ls-surface overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ls-ledger-table">
                    <thead>
                      <tr className="text-left" style={{ color: 'var(--ls-muted)' }}>
                        <th className="py-3 px-4 ls-overline text-xs">Type</th>
                        <th className="py-3 px-4 ls-overline text-xs">By</th>
                        <th className="py-3 px-4 ls-overline text-xs">Service</th>
                        <th className="py-3 px-4 ls-overline text-xs">When</th>
                        <th className="py-3 px-4 ls-overline text-xs text-right">Amount</th>
                        <th className="py-3 px-4 ls-overline text-xs text-right">Balance</th>
                        <th className="py-3 px-4 ls-overline text-xs text-center w-8">
                          <Eye size={11} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {txns.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-8 text-sm" style={{ color: 'var(--ls-muted)' }}>
                            No transactions yet on this wallet.
                          </td>
                        </tr>
                      ) : (
                        txns.map((t) => <TxnRow key={t.txn_id} t={t} />)
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </>
      )}
    </>
  );
};

const LogisticsWalletPage = () => (
  <LogisticsLayout>
    <WalletContent />
  </LogisticsLayout>
);

export default LogisticsWalletPage;
